interface LogoProps {
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

export function Logo({ size = 'medium', className = '' }: LogoProps) {
  const sizeConfig = {
    small: { width: 200, height: 60 },
    medium: { width: 300, height: 90 },
    large: { width: 400, height: 120 },
  };

  const { width, height } = sizeConfig[size];

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 400 120"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        {/* Gradient for the main logo */}
        <linearGradient id="mainGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#3B82F6', stopOpacity: 1 }} />
          <stop offset="50%" style={{ stopColor: '#1D4ED8', stopOpacity: 1 }} />
          <stop
            offset="100%"
            style={{ stopColor: '#1E40AF', stopOpacity: 1 }}
          />
        </linearGradient>

        {/* Gradient for accent elements */}
        <linearGradient id="accentGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style={{ stopColor: '#F59E0B', stopOpacity: 1 }} />
          <stop
            offset="100%"
            style={{ stopColor: '#EF4444', stopOpacity: 1 }}
          />
        </linearGradient>

        {/* Glow effect */}
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Background circle with subtle glow */}
      <circle
        cx="60"
        cy="60"
        r="45"
        fill="url(#mainGradient)"
        opacity="0.1"
        filter="url(#glow)"
      />

      {/* Main bike wheel */}
      <circle
        cx="60"
        cy="60"
        r="35"
        fill="none"
        stroke="url(#mainGradient)"
        strokeWidth="4"
      />

      {/* Spokes with dynamic angles */}
      <g stroke="url(#mainGradient)" strokeWidth="2" opacity="0.8">
        <line x1="60" y1="25" x2="60" y2="95" transform="rotate(0 60 60)" />
        <line x1="60" y1="25" x2="60" y2="95" transform="rotate(45 60 60)" />
        <line x1="60" y1="25" x2="60" y2="95" transform="rotate(90 60 60)" />
        <line x1="60" y1="25" x2="60" y2="95" transform="rotate(135 60 60)" />
      </g>

      {/* Center hub */}
      <circle cx="60" cy="60" r="8" fill="url(#accentGradient)" />

      {/* Speed lines indicating motion */}
      <g
        stroke="url(#accentGradient)"
        strokeWidth="3"
        strokeLinecap="round"
        opacity="0.7"
      >
        <line x1="105" y1="45" x2="125" y2="40" />
        <line x1="105" y1="60" x2="130" y2="60" />
        <line x1="105" y1="75" x2="125" y2="80" />
      </g>

      {/* Power/training indicator */}
      <g transform="translate(25, 25)">
        <rect
          x="0"
          y="0"
          width="4"
          height="15"
          fill="url(#accentGradient)"
          opacity="0.6"
        />
        <rect
          x="6"
          y="5"
          width="4"
          height="10"
          fill="url(#accentGradient)"
          opacity="0.8"
        />
        <rect x="12" y="2" width="4" height="13" fill="url(#accentGradient)" />
      </g>

      {/* Text: TURBO */}
      <text
        x="150"
        y="45"
        fontFamily="Arial, sans-serif"
        fontSize="32"
        fontWeight="bold"
        fill="url(#mainGradient)"
      >
        TURBO
      </text>

      {/* Text: TRAINER */}
      <text
        x="150"
        y="75"
        fontFamily="Arial, sans-serif"
        fontSize="24"
        fontWeight="600"
        fill="#6B7280"
        letterSpacing="2px"
      >
        TRAINER
      </text>

      {/* Small tagline */}
      <text
        x="150"
        y="95"
        fontFamily="Arial, sans-serif"
        fontSize="10"
        fill="#9CA3AF"
        letterSpacing="1px"
      >
        SMART INDOOR CYCLING
      </text>

      {/* Decorative elements */}
      <circle
        cx="380"
        cy="20"
        r="3"
        fill="url(#accentGradient)"
        opacity="0.6"
      />
      <circle cx="370" cy="30" r="2" fill="url(#mainGradient)" opacity="0.4" />
      <circle
        cx="385"
        cy="35"
        r="1.5"
        fill="url(#accentGradient)"
        opacity="0.8"
      />
    </svg>
  );
}
